import { type ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'

import { type AlgoliaRecordStockStatusReason } from '../../../algolia'

import { useProductSearchResultItemContext } from './ProductSearchResultItemContext/productSearchResultItemContext'

type ResultItemStockStatus = {
  bgColor: string
  color: string
  dotColor: string
  message: ReactElement | null
}

const RESULT_ITEM_STOCK_STATUS_DEFAULT = {
  bgColor: '',
  color: '',
  dotColor: '',
  message: null,
}

const resultItemStockStatusMap: Partial<
  Record<AlgoliaRecordStockStatusReason, ResultItemStockStatus>
> = {
  'product.stockStatus.av': {
    bgColor: 'bg-dark-brand',
    color: 'text-dark-brand',
    dotColor: 'bg-[theme(textColor[dark-error])]',
    message: <FormattedMessage id="general.label.product.outOfTrade" />,
  },
  'product.stockStatus.available': {
    bgColor: 'bg-dark-hover-success',
    color: 'text-dark-success',
    dotColor: 'bg-dark-hover-success',
    message: <FormattedMessage id="general.label.product.available" />,
  },
  'product.stockStatus.gratis': {
    bgColor: 'bg-dark-hover-success',
    color: 'text-dark-success',
    dotColor: 'bg-dark-hover-success',
    message: <FormattedMessage id="general.label.product.gratis" />,
  },
  'product.stockStatus.nl': {
    bgColor: 'bg-[theme(textColor[dark-note])]',
    color: 'text-dark-note',
    dotColor: 'bg-[theme(textColor[dark-note])]',
    message: <FormattedMessage id="general.label.product.notAvailable" />,
  },
  'product.stockStatus.notInCatalog': {
    bgColor: 'bg-dark-brand',
    color: 'text-dark-brand',
    dotColor: 'bg-[theme(textColor[dark-error])]',
    message: <FormattedMessage id="general.label.product.notInAssortment" />,
  },
}

const getFullStatus = (
  statusReason: AlgoliaRecordStockStatusReason,
): ResultItemStockStatus => {
  return (
    resultItemStockStatusMap[statusReason] ?? RESULT_ITEM_STOCK_STATUS_DEFAULT
  )
}

/**
 * Checks the stock status of the algolia record, figures out the color and message to display and returns it
 */
export const useResultItemStockStatus = (): ResultItemStockStatus => {
  const { algoliaRecord: item } = useProductSearchResultItemContext()

  return getFullStatus(item.stockStatusReason)
}
